import { useTheme } from '@mui/material/styles';
import { memo, useMemo, useRef, useState } from 'react';

const LINE_HEIGHT = 18;
const FONT_SIZE = 12;
const PAD_Y = 12;
const PAD_X = 12;

type Props = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  /** 1-based line to highlight in the gutter (e.g. from a JSON parse error). */
  errorLine?: number;
  /** Visible height in rows. */
  rows?: number;
  placeholder?: string;
  ariaLabel?: string;
};

/**
 * A lightweight code/JSON textarea with a synced line-number gutter — no extra
 * dependency. Uses `white-space: pre` (no wrapping) so each logical line maps to
 * exactly one row, keeping the gutter aligned and matching the "line N" numbers
 * in JSON parse errors. A given `errorLine` is highlighted in the gutter.
 */
export const JsonEditor = memo(
  ({
    value,
    onChange,
    error,
    errorLine,
    rows = 18,
    placeholder,
    ariaLabel = 'JSON editor',
  }: Props) => {
    const theme = useTheme();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const gutterRef = useRef<HTMLDivElement>(null);
    const [focused, setFocused] = useState(false);

    const lineCount = useMemo(() => value.split('\n').length || 1, [value]);

    const handleScroll = () => {
      if (gutterRef.current && textareaRef.current) {
        gutterRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };

    const borderColor = error
      ? theme.palette.error.main
      : focused
      ? theme.palette.primary.main
      : theme.palette.grey[400];

    const monospace = {
      fontFamily: 'monospace',
      fontSize: FONT_SIZE,
      lineHeight: `${LINE_HEIGHT}px`,
    } as const;

    return (
      <div
        style={{
          display: 'flex',
          height: rows * LINE_HEIGHT + PAD_Y * 2,
          border: `1px solid ${borderColor}`,
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          background: theme.palette.background.paper,
          boxShadow:
            focused && !error
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : 'none',
        }}
      >
        <div
          ref={gutterRef}
          aria-hidden
          style={{
            ...monospace,
            flexShrink: 0,
            padding: `${PAD_Y}px 8px`,
            textAlign: 'right',
            color: theme.palette.text.disabled,
            background: theme.palette.grey[50],
            userSelect: 'none',
            overflow: 'hidden',
            minWidth: 34,
          }}
        >
          {Array.from({ length: lineCount }, (_, i) => i + 1).map((n) => (
            <div
              key={n}
              style={{
                height: LINE_HEIGHT,
                color: n === errorLine ? theme.palette.error.main : undefined,
                fontWeight: n === errorLine ? 700 : undefined,
              }}
            >
              {n}
            </div>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          style={{
            ...monospace,
            flex: 1,
            margin: 0,
            border: 0,
            outline: 'none',
            resize: 'none',
            padding: `${PAD_Y}px ${PAD_X}px`,
            background: 'transparent',
            color: theme.palette.text.primary,
            overflow: 'auto',
            whiteSpace: 'pre',
          }}
        />
      </div>
    );
  }
);

JsonEditor.displayName = 'JsonEditor';
